import { isPlatformBrowser } from '@angular/common';
import * as i0 from '@angular/core';
import { input, signal, inject, ChangeDetectorRef, PLATFORM_ID, Component, ChangeDetectionStrategy } from '@angular/core';
import { NgDocSanitizeHtmlPipe } from '@ng-doc/app/pipes';
import { NgDocThemeService } from '@ng-doc/app/services/theme';
import { NG_DOC_MERMAID } from '@ng-doc/app/tokens';
import { NgDocMagnifierComponent, MagnifierControllerComponent, NgDocSpinnerComponent } from '@ng-doc/ui-kit';
function NgDocMermaidViewerComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵelement(1, "ng-doc-spinner");
    i0.ɵɵelementEnd();
  }
}
function NgDocMermaidViewerComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.error());
  }
}
function NgDocMermaidViewerComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "ng-doc-magnifier", null, 0);
    i0.ɵɵelement(2, "div", 3);
    i0.ɵɵpipe(3, "ngDocSanitizeHtml");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(4, "ng-doc-magnifier-controller", 4);
  }
  if (rf & 2) {
    const magnifier_r2 = i0.ɵɵreference(1);
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("innerHTML", i0.ɵɵpipeBind1(3, 2, ctx_r0.html()), i0.ɵɵsanitizeHtml);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("magnifier", magnifier_r2);
  }
}
let id = 0;
let NgDocMermaidViewerComponent = /*#__PURE__*/(() => {
  class NgDocMermaidViewerComponent {
    constructor() {
      this.graph = input.required();
      this.html = signal('');
      this.pending = signal(true);
      this.error = signal(null);
      this.changeDetectorRef = inject(ChangeDetectorRef);
      this.platform = inject(PLATFORM_ID);
      this.id = `ng-doc-mermaid-viewer-${id++}`;
      const mermaid = inject(NG_DOC_MERMAID, {
        optional: true
      });
      if (!mermaid) {
        throw new Error('Mermaid is not provided. Make sure that you provided mermaid using "provideMermaid" function.');
      }
      this.mermaid = mermaid;
      inject(NgDocThemeService).themeChanges().subscribe(async () => {
        const {
          svg
        } = await this.mermaid.render(this.id, this.graph());
        this.html.set(svg);
      });
    }
    async ngOnInit() {
      if (isPlatformBrowser(this.platform)) {
        try {
          const {
            svg
          } = await this.mermaid.render(this.id, this.graph());
          this.html.set(svg);
          this.pending.set(false);
        } catch (error) {
          this.error.set(error);
          this.pending.set(false);
        }
      }
    }
    static {
      this.ɵfac = function NgDocMermaidViewerComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgDocMermaidViewerComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NgDocMermaidViewerComponent,
        selectors: [["ng-doc-mermaid-viewer"]],
        inputs: {
          graph: [1, "graph"]
        },
        decls: 3,
        vars: 1,
        consts: [["magnifier", ""], [1, "ng-doc-mermaid-preloader"], [1, "ng-doc-mermaid-error"], [1, "ng-doc-mermaid-container", 3, "innerHTML"], [3, "magnifier"]],
        template: function NgDocMermaidViewerComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, NgDocMermaidViewerComponent_Conditional_0_Template, 2, 0, "div", 1)(1, NgDocMermaidViewerComponent_Conditional_1_Template, 2, 1, "div", 2)(2, NgDocMermaidViewerComponent_Conditional_2_Template, 5, 4);
          }
          if (rf & 2) {
            i0.ɵɵconditional(ctx.pending() ? 0 : ctx.error() ? 1 : 2);
          }
        },
        dependencies: [NgDocMagnifierComponent, MagnifierControllerComponent, NgDocSanitizeHtmlPipe, NgDocSpinnerComponent],
        styles: ["[_nghost-%COMP%]{position:relative;display:block;border:var(--ng-doc-mermaid-viewer-border, 1px solid var(--ng-doc-border-color));margin:var(--ng-doc-mermaid-viewer-margin, var(--ng-doc-page-block-margin));background:var(--ng-doc-mermaid-viewer-background, var(--ng-doc-base-8));border-radius:var(--ng-doc-mermaid-viewer-border-radius, var(--ng-doc-base-gutter));min-height:150px}[_nghost-%COMP%]   ng-doc-magnifier-controller[_ngcontent-%COMP%]{position:absolute;bottom:calc(var(--ng-doc-base-gutter) * 2);right:calc(var(--ng-doc-base-gutter) * 2)}[_nghost-%COMP%]   .ng-doc-mermaid-container[_ngcontent-%COMP%]{display:flex;justify-content:center;align-items:center;padding:calc(var(--ng-doc-base-gutter) * 2)}[_nghost-%COMP%]   .ng-doc-mermaid-preloader[_ngcontent-%COMP%]{display:flex;justify-content:center;align-items:center;width:100%;height:120px}[_nghost-%COMP%]   .ng-doc-mermaid-error[_ngcontent-%COMP%]{display:flex;justify-content:center;align-items:center;height:120px;color:var(--ng-doc-alert)}"],
        changeDetection: 0
      });
    }
  }
  return NgDocMermaidViewerComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgDocMermaidViewerComponent };
